import { useAccess } from '@/hooks/useAccess';
import { useIntl } from '@/i18n';
import { getPlanDetail, getPlanUsage } from '@/services/muou-biz';
import { useAuthStore } from '@/store/auth';
import type { PlanSubscriptionDetailVO, PlanUsageVO } from '@/types';
import { Alert, Button, Skeleton } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import PlanCapacityCard from './PlanCapacityCard';

interface PlanCapacityProps {
  onNavigate?: () => void;
}

/** 套餐卡与套餐页共用页面准入，保留只读接口及失败重试。 */
export default function PlanCapacity({ onNavigate }: PlanCapacityProps) {
  const { canEnter } = useAccess();
  const allowed = canEnter('sys.plan');
  const tenantName = useAuthStore((state) => state.tenantName);
  const status = useAuthStore((state) => state.status);
  const intl = useIntl();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [data, setData] = useState<{
    detail: PlanSubscriptionDetailVO;
    usage: PlanUsageVO;
  }>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!allowed || status !== 'authed') return;
    let cancelled = false;
    setData(undefined);
    setFailed(false);
    Promise.all([getPlanDetail(), getPlanUsage()]).then(
      ([detail, usage]) => {
        if (!cancelled) setData({ detail, usage });
      },
      () => {
        if (!cancelled) setFailed(true);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [allowed, status, tenantName, intl.locale, attempt]);
  if (!allowed || status !== 'authed') return null;
  if (failed)
    return (
      <Alert
        type="error"
        title={intl.formatMessage({ id: 'http.requestFailed' })}
        action={
          <Button size="small" onClick={() => setAttempt((value) => value + 1)}>
            {intl.formatMessage({ id: 'common.retry' })}
          </Button>
        }
      />
    );
  if (!data) return <Skeleton active paragraph={{ rows: 2 }} title={false} />;
  return (
    <PlanCapacityCard
      {...data}
      onManage={() => {
        navigate('/sys/plan');
        onNavigate?.();
      }}
    />
  );
}
